import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  TextField,
  Typography,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useQueryClient } from '@tanstack/react-query';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  discardFoundationApplication,
  listFoundationAvailableFunds,
  patchFoundationApplication,
  searchFoundationInstitutions,
  submitFoundationRecommendation,
  toFormAnswerInputs,
  type FoundationApplicationDetail,
  type FoundationAvailableFund,
  type FoundationFormAnswerInput,
  type FoundationInstitutionSearchResult,
  type FoundationStudentContact,
} from '../../../endpoints/foundation';
import { getUserPersonalInfo } from '../../../endpoints/usersTs';
import type { UserPersonalInfoResponse } from '../../../types/users';
import CountrySelector from '../../../Components/CountrySelector';
import StateSelector from '../../../Components/StateSelector';
import UserSearchModal, {
  type User,
} from '../../../Components/UserSearchModal';
import StudentContactFields from './StudentContactFields';
import FoundationFormRenderer from './FoundationFormRenderer';

const AUTOSAVE_MS = 1500;

export type RecommendationEditorMode = 'draft' | 'staff' | 'readonly';

export type RecommendationEditorProps = {
  application: FoundationApplicationDetail;
  mode?: RecommendationEditorMode;
  onSaved?: () => void | Promise<void>;
  onSubmitted?: () => void | Promise<void>;
};

function contactFromApp(
  app: FoundationApplicationDetail
): FoundationStudentContact {
  return {
    student_address_1: app.student_address_1 || '',
    student_address_2: app.student_address_2 || '',
    student_city: app.student_city || '',
    student_state_id: app.student_state_id || '',
    student_postal_code: app.student_postal_code || '',
    student_country_id: app.student_country_id || '',
    student_primary_phone: app.student_primary_phone || '',
    student_primary_phone_type: app.student_primary_phone_type || '',
    student_secondary_phone: app.student_secondary_phone || '',
    student_secondary_phone_type: app.student_secondary_phone_type || '',
  };
}

function contactPayload(c: FoundationStudentContact) {
  return {
    student_address_1: c.student_address_1 || '',
    student_address_2: c.student_address_2 || '',
    student_city: c.student_city || '',
    student_state_id: c.student_state_id || '',
    student_postal_code: c.student_postal_code || '',
    student_country_id: c.student_country_id || '',
    student_primary_phone: c.student_primary_phone || '',
    student_primary_phone_type: c.student_primary_phone_type || '',
    student_secondary_phone: c.student_secondary_phone || '',
    student_secondary_phone_type: c.student_secondary_phone_type || '',
  };
}

function contactFromPersonalInfo(
  info: UserPersonalInfoResponse
): FoundationStudentContact {
  const addr = info.addresses?.[0];
  const cell = (info.cell_phone || '').trim();
  const home = (info.home_phone || '').trim();
  let primary = '';
  let primaryType = '';
  let secondary = '';
  let secondaryType = '';
  if (cell && home) {
    primary = cell;
    primaryType = 'cell';
    secondary = home;
    secondaryType = 'home';
  } else if (cell) {
    primary = cell;
    primaryType = 'cell';
  } else if (home) {
    primary = home;
    primaryType = 'home';
  }
  return {
    student_address_1: addr?.address_1 || '',
    student_address_2: addr?.address_2 || '',
    student_city: addr?.city || '',
    student_state_id: addr?.state_id || '',
    student_postal_code: addr?.postal_code || '',
    student_country_id: addr?.country_id || '',
    student_primary_phone: primary,
    student_primary_phone_type: primaryType,
    student_secondary_phone: secondary,
    student_secondary_phone_type: secondaryType,
  };
}

function formatSavedAt(d: Date): string {
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: Date }
  | { kind: 'error'; message: string };

export default function RecommendationEditor({
  application,
  mode = 'draft',
  onSaved,
  onSubmitted,
}: RecommendationEditorProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const editable = mode !== 'readonly';
  const moneyLocked = mode !== 'draft';
  const academicYear = application.academic_year;

  const [tab, setTab] = useState('student');
  const [studentFirstName, setStudentFirstName] = useState(
    application.student_first_name || ''
  );
  const [studentLastName, setStudentLastName] = useState(
    application.student_last_name || ''
  );
  const [studentEmail, setStudentEmail] = useState(
    application.student_email || ''
  );
  const [studentUserId, setStudentUserId] = useState(
    application.student_user_id || ''
  );
  const [isPeoMember, setIsPeoMember] = useState(
    Boolean(application.student_user_id)
  );
  const [memberSearchOpen, setMemberSearchOpen] = useState(false);
  const [memberLookupError, setMemberLookupError] = useState<string | null>(
    null
  );
  const [contact, setContact] = useState<FoundationStudentContact>(() =>
    contactFromApp(application)
  );
  const [awardedAmount, setAwardedAmount] = useState(
    String(application.awarded_amount || '')
  );
  const [fundId, setFundId] = useState(
    application.commitments?.[0]?.fund_id || ''
  );
  const [provisionalName, setProvisionalName] = useState(
    application.institution?.provisional_name || ''
  );
  const [provisionalCity, setProvisionalCity] = useState(
    application.institution?.provisional_city || ''
  );
  const [provisionalState, setProvisionalState] = useState(
    application.institution?.provisional_state || ''
  );
  const [provisionalCountry, setProvisionalCountry] = useState(
    application.institution?.provisional_country || ''
  );
  const [provisionalWebsite, setProvisionalWebsite] = useState(
    application.institution?.provisional_website || ''
  );
  const [accreditedInstitutionId, setAccreditedInstitutionId] = useState(
    application.institution?.accredited_institution_id || ''
  );
  const [schoolNotListed, setSchoolNotListed] = useState(
    !application.institution?.accredited_institution_id &&
      Boolean(application.institution?.provisional_name)
  );
  const [institutionQuery, setInstitutionQuery] = useState('');
  const [matchedCity, setMatchedCity] = useState(
    application.institution?.matched_city || ''
  );
  const [matchedState, setMatchedState] = useState(
    application.institution?.matched_state_code || ''
  );
  const [answers, setAnswers] = useState<FoundationFormAnswerInput[]>(() =>
    toFormAnswerInputs(application.answers_by_form_kind?.recommendation)
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({ kind: 'idle' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [hydratedId, setHydratedId] = useState(application.id);
  const [hydratedStage, setHydratedStage] = useState(application.stage);

  const dirtyRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingRef = useRef(false);
  const pickedMemberIdRef = useRef(application.student_user_id || '');
  const latestRef = useRef({
    studentFirstName,
    studentLastName,
    studentEmail,
    studentUserId,
    isPeoMember,
    contact,
    awardedAmount,
    fundId,
    provisionalName,
    provisionalCity,
    provisionalState,
    provisionalCountry,
    provisionalWebsite,
    accreditedInstitutionId,
    schoolNotListed,
    answers,
  });

  latestRef.current = {
    studentFirstName,
    studentLastName,
    studentEmail,
    studentUserId,
    isPeoMember,
    contact,
    awardedAmount,
    fundId,
    provisionalName,
    provisionalCity,
    provisionalState,
    provisionalCountry,
    provisionalWebsite,
    accreditedInstitutionId,
    schoolNotListed,
    answers,
  };

  // Re-hydrate when switching application or leaving draft (submit).
  useEffect(() => {
    if (application.id === hydratedId && application.stage === hydratedStage)
      return;
    setHydratedId(application.id);
    setHydratedStage(application.stage);
    setStudentFirstName(application.student_first_name || '');
    setStudentLastName(application.student_last_name || '');
    setStudentEmail(application.student_email || '');
    setStudentUserId(application.student_user_id || '');
    setIsPeoMember(Boolean(application.student_user_id));
    pickedMemberIdRef.current = application.student_user_id || '';
    setContact(contactFromApp(application));
    setAwardedAmount(String(application.awarded_amount || ''));
    setFundId(application.commitments?.[0]?.fund_id || '');
    setProvisionalName(application.institution?.provisional_name || '');
    setProvisionalCity(application.institution?.provisional_city || '');
    setProvisionalState(application.institution?.provisional_state || '');
    setProvisionalCountry(application.institution?.provisional_country || '');
    setProvisionalWebsite(application.institution?.provisional_website || '');
    setAccreditedInstitutionId(
      application.institution?.accredited_institution_id || ''
    );
    setSchoolNotListed(
      !application.institution?.accredited_institution_id &&
        Boolean(application.institution?.provisional_name)
    );
    setInstitutionQuery('');
    setMatchedCity(application.institution?.matched_city || '');
    setMatchedState(application.institution?.matched_state_code || '');
    setAnswers(
      toFormAnswerInputs(application.answers_by_form_kind?.recommendation)
    );
    dirtyRef.current = false;
    setSaveStatus({ kind: 'idle' });
  }, [application, hydratedId, hydratedStage]);

  const { data: fundsData } = useApiGet<{ items: FoundationAvailableFund[] }>(
    ['foundation-available-funds', academicYear],
    () => listFoundationAvailableFunds({ academic_year: academicYear }),
    { enabled: !moneyLocked, retry: 1 }
  );
  const funds = useMemo(() => fundsData?.items ?? [], [fundsData?.items]);

  useEffect(() => {
    if (!fundId && funds.length === 1) {
      setFundId(funds[0].id);
      dirtyRef.current = true;
    }
  }, [funds, fundId]);

  const buildPatchBody = useCallback(() => {
    const cur = latestRef.current;
    const amount = Number(cur.awardedAmount) || 0;
    const achAnswer = cur.answers.find(
      (a) => a.question_key === 'ach_required'
    );
    const body: Record<string, unknown> = {
      student_first_name: cur.studentFirstName,
      student_last_name: cur.studentLastName,
      student_email: cur.studentEmail,
      ach_required: (achAnswer?.value || '').toLowerCase() === 'yes',
      answers: cur.answers,
      ...contactPayload(cur.contact),
    };
    if (!(cur.isPeoMember && !cur.studentUserId)) {
      body.student_user_id = cur.studentUserId || '';
    }
    if (!moneyLocked) {
      if (amount > 0) {
        body.awarded_amount = amount;
        body.fund_splits = cur.fundId ? [{ fund_id: cur.fundId, amount }] : [];
      } else {
        body.fund_splits = [];
      }
    }
    if (cur.accreditedInstitutionId) {
      body.provisional_institution = {
        name: cur.provisionalName.trim(),
        accredited_institution_id: cur.accreditedInstitutionId,
      };
    } else if (
      cur.provisionalName.trim() ||
      cur.schoolNotListed ||
      application.institution?.accredited_institution_id
    ) {
      body.provisional_institution = {
        name: cur.provisionalName.trim(),
        city: cur.provisionalCity.trim(),
        state: cur.provisionalState.trim(),
        country: cur.provisionalCountry.trim(),
        website: cur.provisionalWebsite.trim(),
        accredited_institution_id: '',
      };
    }
    return body;
  }, [application.institution?.accredited_institution_id, moneyLocked]);

  const flushSave = useCallback(async (): Promise<boolean> => {
    if (!editable) return true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!dirtyRef.current) return true;
    if (savingRef.current) return true;

    savingRef.current = true;
    setSaveStatus({ kind: 'saving' });
    try {
      await patchFoundationApplication(application.id, buildPatchBody());
      dirtyRef.current = false;
      setSaveStatus({ kind: 'saved', at: new Date() });
      if (onSaved) await onSaved();
      return true;
    } catch (e: any) {
      setSaveStatus({
        kind: 'error',
        message: e?.message || 'Save failed',
      });
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [application.id, buildPatchBody, editable, onSaved]);

  const scheduleAutosave = useCallback(() => {
    if (!editable) return;
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flushSave();
    }, AUTOSAVE_MS);
  }, [editable, flushSave]);

  const showSearch = editable && !accreditedInstitutionId && !schoolNotListed;
  const { data: institutionSearch } = useApiGet<{
    items: FoundationInstitutionSearchResult[];
  }>(
    ['foundation-institution-search-editor', application.id, institutionQuery],
    () => searchFoundationInstitutions(institutionQuery),
    {
      enabled: showSearch && institutionQuery.trim().length >= 2,
      retry: 0,
    }
  );
  const searchItems = institutionSearch?.items ?? [];

  const pickCatalogSchool = (inst: FoundationInstitutionSearchResult) => {
    if (!editable) return;
    setAccreditedInstitutionId(inst.id);
    setProvisionalName(inst.name);
    setProvisionalCity('');
    setProvisionalState('');
    setProvisionalCountry('');
    setProvisionalWebsite('');
    setSchoolNotListed(false);
    setInstitutionQuery('');
    setMatchedCity(inst.city || '');
    setMatchedState(inst.state_code || '');
    scheduleAutosave();
  };

  const changeSchool = () => {
    if (!editable) return;
    setAccreditedInstitutionId('');
    setSchoolNotListed(false);
    setInstitutionQuery(provisionalName);
    setMatchedCity('');
    setMatchedState('');
    scheduleAutosave();
  };

  const markNotListed = () => {
    if (!editable) return;
    setAccreditedInstitutionId('');
    setSchoolNotListed(true);
    setInstitutionQuery('');
    setMatchedCity('');
    setMatchedState('');
    scheduleAutosave();
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void flushSave();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [flushSave]);

  const markDirty = useCallback(
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: React.SetStateAction<T>) => {
        setter(value);
        scheduleAutosave();
      },
    [scheduleAutosave]
  );

  const handleMemberModalOpen = (open: boolean) => {
    setMemberSearchOpen(open);
    if (!open && !pickedMemberIdRef.current) {
      setIsPeoMember(false);
    }
  };

  const handlePeoMemberChange = (value: string) => {
    if (value === 'yes') {
      setIsPeoMember(true);
      if (!studentUserId) {
        setMemberSearchOpen(true);
      }
      return;
    }
    setIsPeoMember(false);
    setMemberLookupError(null);
    pickedMemberIdRef.current = '';
    if (studentUserId) {
      setStudentUserId('');
      scheduleAutosave();
    }
  };

  const applyMember = async (user: User) => {
    pickedMemberIdRef.current = user.id;
    setMemberSearchOpen(false);
    setMemberLookupError(null);
    setIsPeoMember(true);
    setStudentUserId(user.id);
    const fallbackFirst = user.first_name || '';
    const fallbackLast = user.last_name || '';
    try {
      const info = await getUserPersonalInfo(user.id);
      setStudentFirstName(info.first_name || fallbackFirst);
      setStudentLastName(info.last_name || fallbackLast);
      if (!info.is_partial) {
        setStudentEmail(info.email || '');
        setContact(contactFromPersonalInfo(info));
      }
    } catch (e: any) {
      setStudentFirstName(fallbackFirst);
      setStudentLastName(fallbackLast);
      setMemberLookupError(
        e?.message ||
          'Could not load member contact details. Name was filled; complete the rest manually.'
      );
    }
    scheduleAutosave();
  };

  const handleTabChange = async (_: React.SyntheticEvent, next: string) => {
    await flushSave();
    setTab(next);
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const ok = await flushSave();
      if (!ok) {
        setSubmitError('Save failed — fix and try again before submitting.');
        return;
      }
      const cur = latestRef.current;
      if (!cur.fundId) {
        setSubmitError(
          'Select a fund on the Scholarship tab before submitting.'
        );
        setTab('scholarship');
        return;
      }
      if (
        !cur.studentFirstName.trim() ||
        !cur.studentLastName.trim() ||
        !cur.studentEmail.trim()
      ) {
        setSubmitError(
          'Student name and email are required before submitting.'
        );
        setTab('student');
        return;
      }
      if (cur.isPeoMember && !cur.studentUserId) {
        setSubmitError(
          'Select the P.E.O. member on the Student tab before submitting.'
        );
        setTab('student');
        return;
      }
      if (!cur.accreditedInstitutionId) {
        if (
          !cur.provisionalName.trim() ||
          !cur.provisionalCity.trim() ||
          !cur.provisionalCountry.trim() ||
          !cur.provisionalState.trim()
        ) {
          setSubmitError(
            'Pick an accredited school or enter name, city, state/province, and country.'
          );
          setTab('scholarship');
          return;
        }
      }
      await submitFoundationRecommendation(application.id);
      if (onSubmitted) await onSubmitted();
    } catch (e: any) {
      setSubmitError(e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDiscard = async () => {
    setSubmitError(null);
    setDiscarding(true);
    try {
      await flushSave();
      await discardFoundationApplication(application.id);
      await queryClient.invalidateQueries({
        queryKey: ['foundation-applications'],
      });
      setDiscardOpen(false);
      navigate('/foundation/applicants', { replace: true });
    } catch (e: any) {
      setSubmitError(e?.message || 'Discard failed');
      setDiscardOpen(false);
    } finally {
      setDiscarding(false);
    }
  };

  const statusLabel = (() => {
    switch (saveStatus.kind) {
      case 'saving':
        return 'Saving…';
      case 'saved':
        return `Saved ${formatSavedAt(saveStatus.at)}`;
      case 'error':
        return saveStatus.message;
      default:
        return dirtyRef.current ? 'Unsaved changes' : '';
    }
  })();

  return (
    <Box sx={{ pb: 10 }}>
      {(submitError || saveStatus.kind === 'error') && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            saveStatus.kind === 'error' ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => void flushSave()}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          {submitError ||
            (saveStatus.kind === 'error' ? saveStatus.message : '')}
        </Alert>
      )}

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleTabChange}
            aria-label="Recommendation sections"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Student" value="student" />
            <Tab label="Scholarship" value="scholarship" />
            <Tab label="Recommendation" value="recommendation" />
          </TabList>
        </Box>

        <TabPanel value="student" sx={{ px: 0, pt: 2 }}>
          <Stack spacing={2}>
            <FormControl>
              <FormLabel id="student-peo-member-label">
                Is the student a P.E.O. member?
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="student-peo-member-label"
                value={isPeoMember ? 'yes' : 'no'}
                onChange={(e) => handlePeoMemberChange(e.target.value)}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio disabled={!editable} />}
                  label="Yes"
                  disabled={!editable}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio disabled={!editable} />}
                  label="No"
                  disabled={!editable}
                />
              </RadioGroup>
            </FormControl>
            {isPeoMember && studentUserId && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
              >
                <Typography variant="body2">
                  Linked member:{' '}
                  {`${studentFirstName} ${studentLastName}`.trim() ||
                    studentUserId}
                </Typography>
                {editable && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setMemberSearchOpen(true)}
                    >
                      Change member
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handlePeoMemberChange('no')}
                    >
                      Not a member
                    </Button>
                  </>
                )}
              </Stack>
            )}
            {memberLookupError && (
              <Alert severity="warning">{memberLookupError}</Alert>
            )}
            <TextField
              label="Student first name"
              value={studentFirstName}
              onChange={(e) => markDirty(setStudentFirstName)(e.target.value)}
              required
              disabled={!editable}
              fullWidth
            />
            <TextField
              label="Student last name"
              value={studentLastName}
              onChange={(e) => markDirty(setStudentLastName)(e.target.value)}
              required
              disabled={!editable}
              fullWidth
            />
            <TextField
              label="Student email"
              value={studentEmail}
              onChange={(e) => markDirty(setStudentEmail)(e.target.value)}
              required
              disabled={!editable}
              fullWidth
            />
            <StudentContactFields
              value={contact}
              onChange={markDirty(setContact)}
              disabled={!editable}
            />
          </Stack>
        </TabPanel>

        <TabPanel value="scholarship" sx={{ px: 0, pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Award amount"
              type="number"
              value={awardedAmount}
              onChange={(e) => markDirty(setAwardedAmount)(e.target.value)}
              required
              disabled={!editable || moneyLocked}
              fullWidth
            />
            {moneyLocked ? (
              <TextField
                label="Fund"
                value={
                  application.commitments?.[0]?.fund_name ||
                  application.commitments?.[0]?.fund_id ||
                  fundId
                }
                disabled
                fullWidth
                helperText="Fund and amount are locked after submit."
              />
            ) : (
              <TextField
                select
                label="Fund"
                value={fundId}
                onChange={(e) => markDirty(setFundId)(e.target.value)}
                disabled={!editable}
                fullWidth
                helperText={
                  funds.length === 0
                    ? 'No available scholarship funds for your chairships'
                    : undefined
                }
              >
                {funds.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name} (${Number(f.available_amount).toFixed(2)}{' '}
                    available)
                  </MenuItem>
                ))}
              </TextField>
            )}
            {accreditedInstitutionId ? (
              <Stack spacing={1}>
                <TextField
                  label="Accredited school"
                  value={[
                    application.institution?.matched_name || provisionalName,
                    matchedCity,
                    matchedState,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  disabled
                  fullWidth
                />
                {editable ? (
                  <Button
                    onClick={changeSchool}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Change school
                  </Button>
                ) : null}
              </Stack>
            ) : schoolNotListed ? (
              <>
                <TextField
                  label="Institution name"
                  value={provisionalName}
                  onChange={(e) =>
                    markDirty(setProvisionalName)(e.target.value)
                  }
                  disabled={!editable}
                  fullWidth
                  required
                  helperText="Include city, state/province, and country so review can look it up."
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="City"
                    value={provisionalCity}
                    onChange={(e) =>
                      markDirty(setProvisionalCity)(e.target.value)
                    }
                    disabled={!editable}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Website"
                    value={provisionalWebsite}
                    onChange={(e) =>
                      markDirty(setProvisionalWebsite)(e.target.value)
                    }
                    disabled={!editable}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Stack flex={1} minWidth={0}>
                    <CountrySelector
                      value={provisionalCountry}
                      handleChange={(country: { id?: string } | null) => {
                        markDirty(setProvisionalCountry)(country?.id || '');
                        markDirty(setProvisionalState)('');
                      }}
                      required
                      disabled={!editable}
                    />
                  </Stack>
                  <Stack flex={1} minWidth={0}>
                    <StateSelector
                      value={provisionalState}
                      handleChange={(state: { id?: string } | null) =>
                        markDirty(setProvisionalState)(state?.id || '')
                      }
                      countryId={provisionalCountry}
                      required
                      disabled={!editable}
                    />
                  </Stack>
                </Stack>
                {editable ? (
                  <Button
                    onClick={() => {
                      setSchoolNotListed(false);
                      setInstitutionQuery(provisionalName);
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Search accredited catalog
                  </Button>
                ) : null}
              </>
            ) : (
              <Stack spacing={1}>
                <TextField
                  label="Search accredited schools"
                  value={institutionQuery}
                  onChange={(e) => setInstitutionQuery(e.target.value)}
                  disabled={!editable}
                  fullWidth
                  helperText="Pick a DAPIP school, or mark it not listed if it is not in the catalog."
                />
                {searchItems.slice(0, 8).map((inst) => (
                  <Button
                    key={inst.id}
                    size="small"
                    disabled={!editable}
                    onClick={() => pickCatalogSchool(inst)}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {inst.name}
                    {inst.city || inst.state_code
                      ? ` (${[inst.city, inst.state_code]
                          .filter(Boolean)
                          .join(', ')})`
                      : ''}
                  </Button>
                ))}
                {editable ? (
                  <Button
                    onClick={markNotListed}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    School is not listed
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    School has not been named.
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </TabPanel>

        <TabPanel value="recommendation" sx={{ px: 0, pt: 2 }}>
          <FoundationFormRenderer
            formKind="recommendation"
            academicYear={academicYear}
            value={answers}
            onChange={markDirty(setAnswers)}
            applicationId={application.id}
            disabled={!editable}
          />
        </TabPanel>
      </TabContext>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          mt: 3,
          py: 1.5,
          px: 1,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 2,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography
            variant="caption"
            color={saveStatus.kind === 'error' ? 'error' : 'text.secondary'}
          >
            {statusLabel}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button onClick={() => navigate('/foundation/applicants')}>
              Back to applicants
            </Button>
            {mode === 'draft' && (
              <>
                <Button
                  color="error"
                  disabled={
                    submitting || discarding || saveStatus.kind === 'saving'
                  }
                  onClick={() => setDiscardOpen(true)}
                >
                  Discard draft
                </Button>
                <Button
                  variant="outlined"
                  disabled={
                    submitting || discarding || saveStatus.kind === 'saving'
                  }
                  onClick={() => void flushSave()}
                >
                  Save draft
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    submitting || discarding || saveStatus.kind === 'saving'
                  }
                  onClick={() => void handleSubmit()}
                >
                  Submit ICH
                </Button>
              </>
            )}
            {mode === 'staff' && (
              <Button
                variant="outlined"
                disabled={saveStatus.kind === 'saving'}
                onClick={() => void flushSave()}
              >
                Save
              </Button>
            )}
          </Stack>
        </Stack>
        {mode === 'draft' && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            Changes autosave while you work. Submitting reserves fund budget and
            starts review.
          </Typography>
        )}
        {mode === 'staff' && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            Corrections autosave. Fund and amount stay locked after submit.
          </Typography>
        )}
      </Box>
      <UserSearchModal
        modalOpen={memberSearchOpen}
        setModalOpen={handleMemberModalOpen}
        setUser={(user) => void applyMember(user)}
        userObjectRet
        statusFilter="all-members"
        allowSetToMe={false}
      />
      <Dialog
        open={discardOpen}
        onClose={() => !discarding && setDiscardOpen(false)}
      >
        <DialogTitle>Discard this draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Empty drafts are deleted. Drafts with a student name or answers are
            kept as discarded and hidden from the default applicants list.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={discarding} onClick={() => setDiscardOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            disabled={discarding}
            onClick={() => void handleDiscard()}
          >
            Discard draft
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
